import { CrossIcon, Icon, type IconDefinition } from '@vrplatform/icons';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import {
  Center,
  Group,
  Text,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';

type Props = {
  opened: boolean;
  isDefault: boolean;
  label: string;
  icon?: React.ReactNode;
  rightIcon?: IconDefinition;
  rightIconSpacing?: number;
  loading?: boolean;
  onClick?: () => void;
  resetFilter?: () => void;
  width?: number;
  type?: 'primary' | 'default';
  rightIconSize?: number;
} & Omit<ComponentPropsWithoutRef<'div'>, 'onClick'> & {
    onClick?: () => void;
  };

export const FilterPill = forwardRef<HTMLDivElement, Props>(
  (
    {
      isDefault,
      opened,
      onClick,
      label,
      resetFilter,
      icon,
      width,
      type = 'primary',
      loading,
      rightIcon,
      rightIconSize,
      rightIconSpacing,
      onKeyDown,
      role,
      tabIndex,
      ...props
    },
    ref
  ) => {
    const { colors, primaryColor } = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();

    const isDarkTheme = colorScheme === 'dark';

    const prim = type === 'primary' ? primaryColor : 'gray';
    const canClear = !isDefault && !!resetFilter;

    return (
      <Group
        {...props}
        ref={ref}
        wrap="nowrap"
        gap={rightIconSpacing}
        onClick={onClick}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.defaultPrevented) return;

          if ((event.key === 'Enter' || event.key === ' ') && onClick) {
            event.preventDefault();
            onClick();
          }

          if (
            (event.key === 'Backspace' || event.key === 'Delete') &&
            canClear
          ) {
            event.preventDefault();
            resetFilter();
          }
        }}
        justify="space-between"
        role={onClick ? role || 'button' : role}
        sx={(theme) => ({
          cursor: 'pointer',
          borderStyle: isDefault && !opened ? 'dashed' : 'solid',
          borderRadius: theme.radius.xl,
          borderWidth: '1px',
          height: '2rem',
          padding: '0.25rem 0.5rem',
          minWidth: width ? undefined : 100,
          maxWidth: width ? undefined : 250,
          width: width || undefined,
          boxShadow: opened
            ? `0px 0px 0px 2px ${theme.colors[prim][4]}40`
            : undefined,
          borderColor:
            opened || !isDefault
              ? theme.colors[prim][isDarkTheme ? 4 : prim === 'primary' ? 6 : 4]
              : theme.colors.gray[colorScheme === 'dark' ? 8 : 3],
          backgroundColor: !isDefault
            ? isDarkTheme
              ? theme.colors[prim][8] + 40
              : theme.colors[prim][0] + 70
            : undefined,
        })}
        tabIndex={onClick ? (tabIndex ?? 0) : tabIndex}
      >
        {icon || loading ? (
          <Group gap={rem(8)} pl={rem(3)} wrap="nowrap" maw={'80%'}>
            {(icon || loading) && (
              <Center
                pb={rem(1)}
                sx={(theme) => ({
                  flexShrink: 0,
                  'svg, path': loading
                    ? undefined
                    : {
                        stroke: theme.colors[theme.primaryColor][7],
                      },
                })}
              >
                {loading ? (
                  <LoadingIndicator
                    size={10}
                    color={opened || !isDefault ? undefined : 'gray'}
                  />
                ) : (
                  icon
                )}
              </Center>
            )}
            <Text
              size="xs"
              component="span"
              sx={{
                color: isDefault
                  ? colors.neutral[5]
                  : colors[prim][isDarkTheme ? 4 : 7],
              }}
              truncate="end"
            >
              {label}
            </Text>
          </Group>
        ) : (
          <Text
            size="xs"
            component="span"
            sx={{
              color: isDefault
                ? colors.neutral[5]
                : colors[prim][isDarkTheme ? 4 : 7],
            }}
            truncate="end"
          >
            {label}
          </Text>
        )}

        {isDefault || !resetFilter ? (
          <Icon
            icon={rightIcon || 'ChevronIcon'}
            size={rightIconSize ?? 16}
            color={
              isDefault
                ? colors.neutral[4]
                : colors[prim][type === 'primary' ? 3 : 7]
            }
            sx={{
              flexShrink: 0,
            }}
          />
        ) : (
          <Center
            component="span"
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={(event) => {
              event.stopPropagation();
              resetFilter();
            }}
            sx={{
              width: rem(18),
              height: rem(18),
              cursor: 'pointer',
              flexShrink: 0,
            }}
            title={`Clear ${label}`}
          >
            <CrossIcon size={16} color={colors[prim][3]} />
          </Center>
        )}
      </Group>
    );
  }
);
